import { create } from "zustand"

import { getTenant } from "~/api/tenant"
import { getUser, getUserFeatureFlags, getNpsSurveyStatus } from "~/api/user"
import { EmailListType } from "~/types/engage"
import { ITokenInfo, ThemeMode } from "~/types/global"
import { ITenant } from "~/types/tenant"
import { IUser, IUserFeatureFlags } from "~/types/user"
import { amplitudeSetup } from "~/utils/amplitude"
import { datadogSetup } from "~/utils/datadog"

type NpsSurveyStatus = {
  response_id: string
  eligible: boolean
  survey_id: string
  skip_count: number
}

type GlobalStore = {
  isUserLoading: boolean
  isUserLoaded: boolean
  setIsUserLoading: (isUserLoading: boolean) => void
  openMessageGenie: boolean
  setOpenMessageGenie: (open: boolean) => void
  tokenInfo: ITokenInfo | null
  setTokenInfo: (tokenInfo: ITokenInfo | null) => void
  user: IUser | null
  setUser: (user: IUser) => void
  updateUser: (user: { fname: string; sname: string }) => void
  getUserInfo: (tokenInfo: ITokenInfo | null) => void
  hydrateFromServer: (data: {
    user: IUser
    tenant: ITenant
    featureFlags: IUserFeatureFlags
  }) => void
  appTheme: ThemeMode
  setAppTheme: (theme: ThemeMode) => void
  tenant: ITenant | null
  setTenant: (tenant: ITenant) => void
  extentionInstalled: boolean
  setExtentionInstalled: (val: boolean) => void
  connectEmailProviderModalOpen: boolean
  setConnectEmailProviderModalOpen: (val: boolean) => void
  emailRecipient: any | null
  setEmailRecipient: (recipient: any | null) => void
  emailMessageToSend: string
  setEmailMessageToSend: (val: string) => void
  emailTypeToSend: EmailListType
  setEmailTypeToSend: (val: EmailListType) => void
  userLinkedinUrl: string
  setUserLinkedinUrl: (val: string) => void
  featureFlags: IUserFeatureFlags | null
  setFeatureFlags: (featureFlags: IUserFeatureFlags) => void
  linkedTenantId: number
  setLinkedTenantId: (id: number) => void
  includesCalendar: boolean
  setIncludesCalendar: (val: boolean) => void
  npsSurveyStatus: NpsSurveyStatus | null
  setNpsSurveyStatus: (val: NpsSurveyStatus | null) => void
  fetchNpsSurveyStatus: (tokenInfo: ITokenInfo) => Promise<void>
  isAskOrbitShiftFabMinimized: boolean
  setIsAskOrbitShiftFabMinimized: (val: boolean) => void
}

export const ignoreEmailSet = ["@getorbitx.com"]

const useGlobalStore = create<GlobalStore>()((set, get) => ({
  isUserLoading: false,
  isUserLoaded: false,
  setIsUserLoading: (isUserLoading) => set({ isUserLoading }),
  openMessageGenie: false,
  setOpenMessageGenie: (open) => set({ openMessageGenie: open }),
  tokenInfo: null,
  setTokenInfo: (tokenInfo) => set({ tokenInfo }),
  user: null,
  setUser: (user) => set({ user }),
  updateUser: (user) =>
    set((s) => ({
      user: s.user ? { ...s.user, ...user } : null
    })),
  tenant: null,
  setTenant: (tenant) => set({ tenant }),
  getUserInfo: async (tokenInfo: ITokenInfo | null) => {
    if (!tokenInfo?.token) {
      set({ isUserLoading: false, isUserLoaded: true })
      return
    }
    if (get().isUserLoading) return
    if (!get().user?.id) {
      set({ isUserLoading: true })
    }
    try {
      const [userResponse, tenantResponse, featureFlagsResponse] =
        await Promise.all([
          getUser(tokenInfo),
          getTenant(tokenInfo),
          getUserFeatureFlags(tokenInfo)
        ])

      let userData = null
      if (userResponse.data.success) {
        userData = userResponse.data.data.user
        set({ user: userData })
      }
      if (tenantResponse.data.success) {
        set({ tenant: tenantResponse.data.data })
      }
      if (featureFlagsResponse.data.success) {
        set({ featureFlags: featureFlagsResponse.data.data })
      }
      if (userData) {
        const eventData = {
          user: {
            id: userData.id,
            name:
              `${userData.fname || ""} ${userData.sname || ""}`.trim() ||
              "user-name",
            email: userData.email
          },
          tenantId: userData.tenant_id
        }
        amplitudeSetup(eventData)
        if (
          ![1].includes(+userData?.tenant_id) ||
          !ignoreEmailSet.some((ignoreEmail) =>
            userData?.email.includes(ignoreEmail)
          )
        ) {
          datadogSetup(eventData)
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      // TODO: handle error
    } finally {
      set({ isUserLoading: false, isUserLoaded: true })
    }
  },
  hydrateFromServer: (data) => {
    set({
      user: data.user,
      tenant: data.tenant,
      featureFlags: data.featureFlags,
      isUserLoading: false,
      isUserLoaded: true
    })
    const eventData = {
      user: {
        id: String(data.user.id),
        name:
          `${data.user.fname || ""} ${data.user.sname || ""}`.trim() ||
          "user-name",
        email: data.user.email
      },
      tenantId: String(data.user.tenant_id)
    }
    amplitudeSetup(eventData)
    if (
      ![1].includes(+data.user?.tenant_id) ||
      !ignoreEmailSet.some((ignoreEmail) =>
        data.user?.email.includes(ignoreEmail)
      )
    ) {
      datadogSetup(eventData)
    }
  },
  appTheme: "light",
  setAppTheme: (theme) => set({ appTheme: theme }),
  extentionInstalled: false,
  setExtentionInstalled: (val) => set({ extentionInstalled: val }),
  connectEmailProviderModalOpen: false,
  setConnectEmailProviderModalOpen: (val) =>
    set({ connectEmailProviderModalOpen: val }),
  emailRecipient: null,
  setEmailRecipient: (recipient) => set({ emailRecipient: recipient }),
  emailMessageToSend: "",
  setEmailMessageToSend: (val) => set({ emailMessageToSend: val }),
  emailTypeToSend: EmailListType.PLATFORM,
  setEmailTypeToSend: (val) => set({ emailTypeToSend: val }),
  userLinkedinUrl: "",
  setUserLinkedinUrl: (val) => set({ userLinkedinUrl: val }),
  featureFlags: null,
  setFeatureFlags: (flags) =>
    set((s) => ({ featureFlags: { ...s.featureFlags, ...flags } })),
  linkedTenantId: 0,
  setLinkedTenantId: (id) => set({ linkedTenantId: id }),
  includesCalendar: false,
  setIncludesCalendar: (val) => set({ includesCalendar: val }),
  npsSurveyStatus: null,
  setNpsSurveyStatus: (val) => set({ npsSurveyStatus: val }),
  isAskOrbitShiftFabMinimized: false,
  setIsAskOrbitShiftFabMinimized: (val) =>
    set({ isAskOrbitShiftFabMinimized: val }),
  fetchNpsSurveyStatus: async (tokenInfo: ITokenInfo) => {
    try {
      const response = await getNpsSurveyStatus(tokenInfo)
      if (response.data.success) {
        set({ npsSurveyStatus: response.data.data })
      }
    } catch {
      set({ npsSurveyStatus: null })
    }
  }
}))

export default useGlobalStore
